import { useLingui } from "@lingui/react";
import { Typography, ToggleButton, Box, styled, ToggleButtonGroup, toggleButtonGroupClasses, FormHelperText, FormControlLabel, RadioGroup, Badge } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { AlertConfigurationForm } from "../../schemas/alertConfigurationSchema";
import { AlertConfigurationV2, DeviceProfile } from "@/__generated__/graphql";
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { getMetricIcon } from "@features/alertsConfigurationV2/utils/metricUtils";
import { getMetricFieldAttributes } from "@/utils/attributes/metricField";

interface MetricSelectionStepProps {
  metrics: AlertConfigurationV2[]
  deviceProfile?: Partial<DeviceProfile> 
}

const MetricSelectionStep = ({ metrics, deviceProfile }: MetricSelectionStepProps) => {
  const { _, i18n } = useLingui();
  const { control, watch } = useFormContext<AlertConfigurationForm>();
  const selectedMetric = watch('metric'); 

  return (
    <div>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 20
        }}
      >

      <Box mb={2}>
        <Typography variant='h5'>
          Configuración de métricas a notificar
        </Typography>
  
        <Typography variant='h6' mb={1}>
          Selecciona una métrica asociada al dispositivo "{deviceProfile?.name}" (ID {deviceProfile?.id})
        </Typography>

        <Typography variant='body1'>
          Seleccione la métrica que desea configurar (puede agregar o editar métricas): 
        </Typography>
      </Box>

      <Controller
        name='metric'
        control={control}
        render={({ field, fieldState: { error } }) => (    
          <Box>
            <Box 
              sx={{ 
                display: 'flex',
                flexWrap: 'wrap',
                gap: 3,
                justifyContent: 'center',
              }}
            >
              {metrics.map(metric => {
                const isSelected = selectedMetric === metric.metricName.toUpperCase();
                const hasConfigurations = metric.configurations.length > 0;
                const metricValue = metric.metricName.toUpperCase();
                const IconComponent = getMetricIcon(metric.metricName);
                
                return (
                  <Badge
                    key={metric.metricName}
                    badgeContent={
                      hasConfigurations 
                        ? (<><EditIcon /><Typography>editar</Typography></>)
                        : (<><AddIcon /><Typography>agregar</Typography></>)
                    }
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    sx={{
                      '& .MuiBadge-badge': { 
                        top: 0, 
                        right: 80,
                        fontSize: 20,
                        height: 30,
                        width: 120,
                        backgroundColor:  hasConfigurations ?  '#D4D4D4' : '#669F36',
                        color: hasConfigurations ? '#000000' : '#FFFFFF' 
                      }
                    }}
                  >
                    <MetricCard 
                      selected={isSelected}
                      hasConfigurations={hasConfigurations}
                      onClick={() => field.onChange(metricValue)}
                      role="radio"
                      aria-checked={isSelected}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          field.onChange(metricValue);
                        }
                      }}
                    >    
                      <IconComponent
                        sx={{ 
                          fontSize: 56,
                          color: isSelected ? 'primary.main' : 'white',
                          transition: 'all 0.3s ease',
                        }} 
                      />
                      
                      <Typography 
                        variant="body1" 
                        fontWeight={isSelected ? 'bold' : 'medium'}
                        color={isSelected ? 'primary.main' : 'white'}
                        sx={{ 
                          textTransform: 'capitalize',
                          textAlign: 'center',
                        }}
                      >
                        {getMetricFieldAttributes(metric.metricName, i18n).dataLabel}
                      </Typography>
                     </MetricCard>
                  </Badge>
                );
              })}
            </Box>
            
            {error && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <FormHelperText error sx={{ fontSize: '0.875rem' }}>
                  {error.message}
                </FormHelperText>
              </Box>
            )}
          </Box>
        )}
      />      
      </Box>


    </div>
  );
};

const MetricCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'hasConfigurations'
})<{ selected: boolean; hasConfigurations: boolean }>(({ theme, selected, hasConfigurations }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: 160,
  height: 160,
  border: '2px solid white',
  borderRadius: 8,
  gap: 10,
  padding: theme.spacing(2),
  backgroundColor: selected ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
}))

export default MetricSelectionStep;
